import { Alert, Box, Button, Card, CardContent, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { PhoneInput, defaultCountries, parseCountry } from 'react-international-phone'
import { useTranslation } from 'react-i18next'

const phoneCountries = defaultCountries.filter((c) => ['us', 'ca'].includes(parseCountry(c).iso2))

import { fetchUserDetails, updateUserName, updateUserPhone } from '../api/services/apiService'
import { setUserDetails } from '../store/authSlice'
import { useAppDispatch } from '../store/store.hooks'
import { logger } from '../utils/logger'
import { useUserSettingsForm } from './hooks/useUserSettingsForm'
import { useUserDetails } from './hooks/user.hooks'
import FormRow from './ui/FormRow'

const UserSettings = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const userDetails = useUserDetails()

  const [loading, setLoading] = useState(false)
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const frontendUrl = import.meta.env.VITE_FRONTEND_URL
  const tenantSubdomain = import.meta.env.VITE_ENTRA_TENANT_SUBDOMAIN
  const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID
  const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID

  if (!frontendUrl) throw 'Invalid configuration. Please set VITE_FRONTEND_URL.'
  if (!tenantSubdomain) throw 'Invalid configuration. Please set VITE_ENTRA_TENANT_SUBDOMAIN.'
  if (!tenantId) throw 'Invalid configuration. Please set VITE_ENTRA_TENANT_ID.'
  if (!clientId) throw 'Invalid configuration. Please set VITE_ENTRA_CLIENT_ID.'

  useEffect(() => {
    if (!userDetails) {
      fetchData()
    }
  }, [userDetails])

  const fetchData = async () => {
    try {
      const userData = await fetchUserDetails()
      dispatch(setUserDetails(userData))
    } catch (error) {
      logger.error('Error fetching user details:', error)
    }
  }
  const {
    firstName,
    lastName,
    phone,
    errors,
    profileChanged,
    handleFirstNameChange,
    handleLastNameChange,
    handlePhoneChange,
    handleResetInformations,
  } = useUserSettingsForm()

  const handleSaveProfile = async () => {
    const finalFirstName = firstName?.trim() || userDetails?.firstName
    const finalLastName = lastName?.trim() || userDetails?.lastName

    if (!finalFirstName || !finalLastName) {
      setNotification({ type: 'error', message: t('userSettingsPage.invalidName') })
      return
    }

    setLoading(true)
    try {
      if (finalFirstName !== userDetails?.firstName || finalLastName !== userDetails?.lastName) {
        await updateUserName(finalFirstName, finalLastName)
      }

      if (phone !== userDetails?.phoneNumber) {
        await updateUserPhone(phone)
      }

      const updatedData = await fetchUserDetails()
      dispatch(setUserDetails(updatedData))

      setNotification({ type: 'success', message: t('userSettingsPage.successSave') })
    } catch {
      setNotification({ type: 'error', message: t('userSettingsPage.failSave') })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 600, color: 'secondary.main' }}>
        {t('userSettingsPage.title')}
      </Typography>

      {notification && (
        <Alert severity={notification.type} onClose={() => setNotification(null)} sx={{ mb: 3 }}>
          {notification.message}
        </Alert>
      )}

      {/* Profile Information Card */}
      <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: 'secondary.main' }}>
            {t('userSettingsPage.profileSection')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <FormRow label={t('userSettingsPage.organizationName')}>
              <Typography sx={{ pt: 1 }}>{userDetails?.organizationName}</Typography>
            </FormRow>

            <FormRow label={t('userSettingsPage.email')}>
              <Typography sx={{ pt: 1 }}>{userDetails?.email}</Typography>
            </FormRow>

            <FormRow label={t('userSettingsPage.firstName')} pt={2}>
              <TextField
                fullWidth
                value={firstName}
                onChange={(e) => handleFirstNameChange(e.target.value)}
                error={!!errors.firstName}
                helperText={errors.firstName}
                size="small"
              />
            </FormRow>

            <FormRow label={t('userSettingsPage.lastName')} pt={2}>
              <TextField
                fullWidth
                value={lastName}
                onChange={(e) => handleLastNameChange(e.target.value)}
                error={!!errors.lastName}
                helperText={errors.lastName}
                size="small"
              />
            </FormRow>

            <FormRow label={t('userSettingsPage.phoneNumber')} pt={2}>
              <PhoneInput
                defaultCountry="ca"
                value={phone}
                onChange={handlePhoneChange}
                countries={phoneCountries}
                style={{ width: '100%' }}
                inputStyle={{
                  width: '100%',
                  height: '40px',
                  fontSize: '1rem',
                  borderRadius: '4px',
                  border: errors.phone ? '1px solid #d32f2f' : '1px solid rgba(0,0,0,0.23)',
                }}
                countrySelectorStyleProps={{ buttonStyle: { height: '40px' } }}
              />
              {errors.phone && (
                <Typography variant="caption" color="error">{errors.phone}</Typography>
              )}
            </FormRow>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={handleResetInformations} disabled={loading || !profileChanged}>
              {t('userSettingsPage.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveProfile}
              disabled={loading || !profileChanged || !!errors.firstName || !!errors.lastName || !!errors.phone}
              sx={{
                bgcolor: 'primary.main',
                color: 'secondary.main',
                fontWeight: 600,
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              {t('userSettingsPage.save')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

export default UserSettings
